import api from "./api";
import { Nullable } from "../interfaces/GenericTypes";
import {
  BCconsultInterface,
  BCsendB2PInterface,
  Banco,
  OutC2P,
} from "../interfaces";
import { bancaribeFormat } from "../utils";

const baseURL = `${process.env.REACT_APP_API_HOST}/Bancaribe`;

/**
 * Memoized fetch of both bank lists. The promise is stored, not the value, and
 * it is stored BEFORE the first await, which is the whole point: the previous
 * version only wrote its "already fetched" marker once both requests had come
 * back, so during those two latencies the module was indistinguishable from
 * cold and every overlapping caller started its own pair of requests.
 *
 * Promise.all also fixes the serial b2p -> c2p cascade: the caller only ever
 * uses one of the two lists, but both are fetched, and they used to be fetched
 * one after the other.
 */
let banksPromise: Promise<[Banco[], Banco[]]> | null = null;
let banksExpireAt = 0;
const BANKS_TTL_MS = 24 * 60 * 60 * 1000;

// Send B2P transactions Bancaribe
export const sendB2PBC = async (
  bank: string, // ej - "0114", code for Bancaribe
  ci: string, // Needs type V, E or P, ej V12345678
  amount: number, // Transaction amount accepts 2 decimals
  phone: string, // Customer phone, ej 04141234567
  concept: string = "Pago B2P", // Transaction's number guide
  invoice: string = "Factura" // Optional - Business preference //token de sesion de usuario
): Promise<Nullable<BCsendB2PInterface>> => {
  const amountString = bancaribeFormat(amount);
  try {
    const response = await api.post(
      `${baseURL}/B2P?bank=${bank}&ci=${ci}&amount=${amountString}&phone=${phone}&concept=${concept}invoice=${invoice}`
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Send and cancel C2P transactions Bancaribe
export const sendC2PBC = async (
  bank: string, // ej - "0114", code for Bancaribe
  ci: string, // Needs type V, E or P, ej V12345678
  amount: number, // Transaction amount accepts 2 decimals
  phone: string, // Customer phone, ej 04141234567
  otp: string = "", // OTP code - Customer confirmation code
  ref: string = "", // Reference number only needed to cancel transaction
  concept: string = "", // Transaction's number guide
  cancel: boolean = false // Cancel transaction //token de sesion de usuario
): Promise<Nullable<OutC2P>> => {
  const amountString = bancaribeFormat(amount);
  try {
    const response = await api.post(
      `${baseURL}/C2P?bank=${bank}&ci=${ci}&amount=${amountString}&phone=${phone}&concept=${concept}&otp=${otp}&reference=${ref}&cancel=${cancel}`
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Consult B2P, P2C, C2P and TRF Bancaribe
export const consultBC = async (
  ci: string, // Needs type V, E or P, ej V12345678
  amount: number, // Transaction amount accepts 2 decimals
  phone: string, // Customer's phone if P2C or C2P, Business phone if B2P, empty if TRF
  ref: string, // Transaction reference
  trx: string = "PM", // ej - PM: for B2P, P2C or C2P. TRF: Transference
  invoice: string = "" // Optional - Business preference //token de sesion de usuario
): Promise<Nullable<BCconsultInterface>> => {
  const amountString = bancaribeFormat(amount);
  try {
    const response = await api.post(
      `${baseURL}/Consult?ci=${ci}&amount=${amountString}&phone=${phone}&reference=${ref}&trx=${trx}&invoice=${invoice}`
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return null;
};

// Get and list banks for B2P and C2P Bancaribe
const getBankBC = async (
  type: string // b2p or c2p //token de sesion de usuario
): Promise<Banco[]> => {
  try {
    const response = await api.get(`${baseURL}/Banks?type=${type}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {}

  return [];
};

export const listBankBC = async (type: string, token?: string) => {
  if (banksPromise === null || Date.now() > banksExpireAt) {
    banksExpireAt = Date.now() + BANKS_TTL_MS;
    banksPromise = Promise.all([getBankBC("b2p"), getBankBC("c2p")]);
  }

  let b2p: Banco[];
  let c2p: Banco[];
  try {
    [b2p, c2p] = await banksPromise;
  } catch (error) {
    // Never leave a rejected promise memoized: it would keep answering with the
    // same failure until the TTL expired.
    banksPromise = null;
    banksExpireAt = 0;
    return [];
  }

  // getBankBC swallows its own errors and falls back to [], so an empty list is
  // indistinguishable from a failed request. Drop the memo so the next caller
  // retries instead of being stuck with empty bank dropdowns for a day.
  if (b2p.length === 0 || c2p.length === 0) {
    banksPromise = null;
    banksExpireAt = 0;
  }

  return type === "b2p" ? b2p : c2p;
};
